/**
 * A Stripe payout, and the customers inside it.
 *
 * Cards are the awkward half of getting paid. A bank transfer arrives as
 * itself — one credit, one customer, one payment request. A card is charged at
 * Stripe, held two or three days, and then swept into a payout with every
 * other card that cleared in the same window: one credit, net of fees, with no
 * name on it. Money In has never been able to say what such a line is, and
 * three of them have been waiting for somebody to split them by hand.
 *
 * Stripe says what is inside a payout, and the `stripe` action records it. The
 * two questions left are arithmetic, and they are here:
 *
 *   1. **which bank credit is this payout?** Its amount, to the cent, on or
 *      about the day Stripe said it would arrive. One candidate or none —
 *      two credits for the same amount in the same week is a person's
 *      question, not a coin toss.
 *   2. **whose is each charge?** The email the card was billed under, matched
 *      against the payment requests Mercury says were paid. Mercury bills the
 *      job's contact email, so the email *is* the join — but it is never
 *      enough on its own here: the amount has to fit the request too, and
 *      exactly one request may fit.
 *
 * Pure. The writing is `settleStripePayouts` in apps/web/src/lib/payments.ts,
 * which carves the bank line up through the same `split_mercury_deposit` a
 * person's split goes through, and confirms each part through the same
 * `confirmDeposit`. A payout of one charge is not carved — see
 * `payoutNeedsSplitting` — and the line itself is confirmed instead.
 */

import { type BankLine, type PaidRequest } from "./depositLink.js";
import { cents, formatMoney } from "./money.js";

/** One customer's charge inside a payout, as `stripe_payout_charges` holds it. */
export interface PayoutCharge {
  id: string;
  stripePayoutId: string;
  stripeChargeId: string;
  /** What the customer was charged, Stripe's number. */
  grossCents: number;
  /** What this charge contributed to the bank credit. Stripe's number. */
  netCents: number;
  email: string | null;
  chargedAt: string | null;
}

/** A sweep, as its charges describe it. */
export interface Payout {
  stripePayoutId: string;
  amountCents: number;
  /** `YYYY-MM-DD`, the day Stripe said the money would land. */
  arrivalDate: string | null;
  charges: PayoutCharge[];
}

/**
 * How far a credit may sit from the arrival date Stripe promised.
 *
 * Stripe's `arrival_date` is an estimate; a bank posts when it posts, and a
 * weekend moves it. Three days each way covers that without widening the
 * window to the point where two payouts could plausibly be the same credit.
 */
export const ARRIVAL_SLACK_DAYS = 3;

/** The payouts a set of charge rows describes, newest arrival first. */
export function payoutsFromCharges(
  rows: readonly (PayoutCharge & {
    payoutAmountCents: number | null;
    payoutArrivalDate: string | null;
  })[],
): Payout[] {
  const byPayout = new Map<string, Payout>();
  for (const row of rows) {
    const existing = byPayout.get(row.stripePayoutId);
    if (existing) {
      existing.charges.push(row);
      continue;
    }
    byPayout.set(row.stripePayoutId, {
      stripePayoutId: row.stripePayoutId,
      amountCents: row.payoutAmountCents ?? 0,
      arrivalDate: row.payoutArrivalDate,
      charges: [row],
    });
  }
  return [...byPayout.values()].sort((a, b) =>
    (b.arrivalDate ?? "").localeCompare(a.arrivalDate ?? ""),
  );
}

/** Whole days between a posting time and an arrival date, or null. */
function daysApart(postedAt: string | null, arrival: string | null) {
  if (!postedAt || !arrival) return null;
  const posted = new Date(postedAt).getTime();
  const expected = new Date(`${arrival}T00:00:00Z`).getTime();
  if (Number.isNaN(posted) || Number.isNaN(expected)) return null;
  return Math.abs(posted - expected) / 86_400_000;
}

export type PayoutOnTheStatement =
  | { kind: "one"; line: BankLine }
  | { kind: "none"; why: string }
  | { kind: "several"; why: string };

/**
 * Which bank credit this payout is.
 *
 * The amount has to be exact — a payout is the sum of its parts and the bank
 * does not round it — and the credit has to have posted within a few days of
 * the arrival date. Two candidates means two credits for the same amount in
 * the same few days, which happens when two payouts are the same size, and
 * then nobody guesses: both lines stay on Money In for a person.
 */
/**
 * Does this credit look like it came from Stripe at all?
 *
 * A payout lands as "STRIPE; TRANSFER; COPPERRIDGE ROOFING EX" — the processor
 * is on the line, every time. Without this test any credit of the right size
 * within three days was fair game: an ACH from a customer, a refund from a
 * supplier, an owner's transfer, carved up among card customers' jobs (Josh,
 * 2026-09-13).
 */
export function looksLikeStripe(line: BankLine): boolean {
  const words = [line.counterparty, line.description, line.externalMemo]
    .map((value) => (value ?? "").toLowerCase())
    .join(" ");
  return words.includes("stripe");
}

export function payoutOnTheStatement(
  payout: Payout,
  lines: readonly BankLine[],
): PayoutOnTheStatement {
  const fits = lines.filter((line) => {
    if (line.amountCents !== payout.amountCents) return false;
    // The processor's name is on its own transfers, and this is the only
    // thing standing between a card payout and an unrelated credit of the
    // same size.
    if (!looksLikeStripe(line)) return false;
    const apart = daysApart(line.postedAt, payout.arrivalDate);
    // A line with no posting time, or a payout with no arrival date, is
    // matched on the amount alone. That is deliberately weaker, and it is why
    // "several" exists.
    return apart === null || apart <= ARRIVAL_SLACK_DAYS;
  });

  if (fits.length === 1) return { kind: "one", line: fits[0]! };
  if (fits.length > 1) {
    return {
      kind: "several",
      why: `${fits.length} credits of ${formatMoney(cents(payout.amountCents))} landed around ${payout.arrivalDate ?? "then"}, so which one this payout is, is yours to say.`,
    };
  }
  return {
    kind: "none",
    why: `No credit of ${formatMoney(cents(payout.amountCents))} has arrived yet.`,
  };
}

/** A charge, and the payment request it settles. */
export interface SettledCharge {
  charge: PayoutCharge;
  request: PaidRequest;
}

/** A charge nobody could account for, and why. */
export interface UnsettledCharge {
  charge: PayoutCharge;
  why: string;
}

const sameEmail = (a: string | null, b: string | null) =>
  !!a && !!b && a.trim().toLowerCase() === b.trim().toLowerCase();

/**
 * Whose each charge is.
 *
 * The email the card was billed under against the email on the job — Mercury
 * invoices the job's contact, so the two are the same string or the charge is
 * not ours. The amount then has to fit: what Mercury says the request was paid
 * is what the card was charged, surcharge included, and a card that was
 * charged something else is a different payment.
 *
 * Two requests fitting one charge — the same customer with two identical
 * payments outstanding — settles neither. A request is claimed once across the
 * whole payout, for the same reason.
 */
export function chargesToRequests(
  charges: readonly PayoutCharge[],
  requests: readonly PaidRequest[],
): { settle: SettledCharge[]; leftovers: UnsettledCharge[] } {
  const settle: SettledCharge[] = [];
  const leftovers: UnsettledCharge[] = [];
  const claimed = new Set<string>();

  for (const charge of charges) {
    if (!charge.email) {
      leftovers.push({
        charge,
        why: "Stripe did not say which email this card was billed under.",
      });
      continue;
    }

    const theirs = requests.filter(
      (request) =>
        !claimed.has(request.id) &&
        sameEmail(request.customerEmail, charge.email),
    );
    if (theirs.length === 0) {
      leftovers.push({
        charge,
        why: `No payment request was paid by ${charge.email}.`,
      });
      continue;
    }

    const fitting = theirs.filter((request) => {
      const paid = request.amountPaidCents ?? request.amountCents;
      return paid != null && paid === charge.grossCents;
    });

    if (fitting.length === 1) {
      const request = fitting[0]!;
      claimed.add(request.id);
      settle.push({ charge, request });
      continue;
    }

    leftovers.push({
      charge,
      why:
        fitting.length === 0
          ? `${charge.email} paid a request, but not ${formatMoney(cents(charge.grossCents))}.`
          : `${charge.email} has ${fitting.length} payments of ${formatMoney(cents(charge.grossCents))} outstanding, so this one is yours to say.`,
    });
  }

  return { settle, leftovers };
}

/**
 * Do the charges account for the whole credit?
 *
 * They must, to the cent, before the bank line is carved up: a split whose
 * parts do not add up to the line is money recorded against a job that never
 * arrived. A refund or an adjustment swept into the same payout is the usual
 * reason they do not, and that is a payout for a person to split by hand.
 */
export function chargesAccountForTheLine(
  payout: Payout,
  line: BankLine,
): { ready: boolean; why: string | null } {
  const nets = payout.charges.reduce((sum, charge) => sum + charge.netCents, 0);
  if (nets === line.amountCents) return { ready: true, why: null };
  return {
    ready: false,
    why: `Stripe's charges add up to ${formatMoney(cents(nets))}, and the credit is ${formatMoney(cents(line.amountCents))}. Something else — a refund, an adjustment — is in this payout, so it needs splitting by hand.`,
  };
}

/**
 * Does the bank line have to be carved up at all?
 *
 * Only when more than one customer is in it. A payout of a single charge is
 * that charge — the whole credit is one customer's money, net of Stripe's fee
 * — and it is probably the commonest payout this company gets. There is
 * nothing to split, and `split_mercury_deposit` refuses a split of one part,
 * so such a line is confirmed whole, against the request its one charge
 * settles, with the charge's gross as what the job was owed.
 */
export function payoutNeedsSplitting(payout: Payout): boolean {
  return payout.charges.length > 1;
}
